<script setup>
import { ref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close', 'created']);

const store = useStore();
const { t } = useI18n();

const show = ref(true);
const name = ref('');
const adminName = ref('');
const adminEmail = ref('');

const uiFlags = useMapGetter('reseller/getUIFlags');

const rules = {
  name: { required },
  adminName: { required },
  adminEmail: { required, email },
};

const v$ = useVuelidate(rules, { name, adminName, adminEmail });

const onClose = () => {
  emit('close');
};

const submit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    await store.dispatch('reseller/create', {
      name: name.value,
      admin_email: adminEmail.value,
      admin_name: adminName.value,
    });
    useAlert(t('REVENDA.SUB_ACCOUNTS.CREATED'));
    emit('created');
    emit('close');
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('REVENDA.SUB_ACCOUNTS.CREATE_ERROR')
    );
  }
};
</script>

<template>
  <woot-modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="t('REVENDA.SUB_ACCOUNTS.NEW')" />
      <form class="flex flex-col items-start w-full" @submit.prevent="submit">
        <div class="w-full">
          <label :class="{ error: v$.name.$error }">
            {{ t('REVENDA.SUB_ACCOUNTS.NAME') }}
            <input v-model="name" type="text" @input="v$.name.$touch" />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.adminName.$error }">
            {{ t('REVENDA.SUB_ACCOUNTS.ADMIN_NAME') }}
            <input
              v-model="adminName"
              type="text"
              @input="v$.adminName.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.adminEmail.$error }">
            {{ t('REVENDA.SUB_ACCOUNTS.ADMIN_EMAIL') }}
            <input
              v-model="adminEmail"
              type="email"
              @input="v$.adminEmail.$touch"
            />
          </label>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <Button
            faded
            slate
            type="reset"
            :label="t('REVENDA.SUB_ACCOUNTS.CANCEL')"
            @click.prevent="onClose"
          />
          <Button
            type="submit"
            :label="t('REVENDA.SUB_ACCOUNTS.CREATE')"
            :disabled="v$.$invalid || uiFlags.isCreating"
            :is-loading="uiFlags.isCreating"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
